/**
 * IndexedDB-backed implementation of the offline queue store.
 *
 * Uses `idb` for a small, typed wrapper around IndexedDB. Database name is
 * `LithosFieldQueueDB` — independent of the existing `BarcodeOfflineDB` so
 * the legacy barcode flow continues to work untouched.
 *
 * Schema:
 *   ops object store, keyPath 'id'.
 *   Indexes:
 *     - by_status         (single string)
 *     - by_nextAttemptAt  (number)  — runner pulls earliest-first
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb'

import type { EnqueueOptions, OfflineQueueStore, QueuedOp } from './types.js'

const DB_NAME = 'LithosFieldQueueDB'
const DB_VERSION = 1
const STORE = 'ops'

interface FieldQueueSchema extends DBSchema {
  ops: {
    key: string
    value: QueuedOp
    indexes: {
      by_status: string
      by_nextAttemptAt: number
    }
  }
}

let dbPromise: Promise<IDBPDatabase<FieldQueueSchema>> | null = null
function getDB() {
  if (!dbPromise) {
    dbPromise = openDB<FieldQueueSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: 'id' })
          store.createIndex('by_status', 'status')
          store.createIndex('by_nextAttemptAt', 'nextAttemptAt')
        }
      },
    })
  }
  return dbPromise
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  // Tiny fallback for older browsers; not cryptographically strong but fine for client-side ids.
  return `op_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

function makeStore(): OfflineQueueStore {
  const listeners = new Set<() => void>()
  const notify = () => listeners.forEach((l) => l())

  async function enqueue<P>(opts: EnqueueOptions<P>): Promise<QueuedOp<P>> {
    const now = Date.now()
    const op: QueuedOp<P> = {
      id: uuid(),
      operation: opts.operation,
      payload: opts.payload,
      description: opts.description,
      enqueuedAt: now,
      nextAttemptAt: now,
      attempts: 0,
      lastError: null,
      status: 'pending',
    }
    const db = await getDB()
    await db.put(STORE, op as QueuedOp)
    notify()
    return op
  }

  async function list(): Promise<QueuedOp[]> {
    const db = await getDB()
    return db.getAll(STORE)
  }

  async function getById(id: string): Promise<QueuedOp | null> {
    const db = await getDB()
    const result = await db.get(STORE, id)
    return result ?? null
  }

  async function remove(id: string): Promise<void> {
    const db = await getDB()
    await db.delete(STORE, id)
    notify()
  }

  async function cancel(id: string): Promise<void> {
    const db = await getDB()
    const op = await db.get(STORE, id)
    if (!op) return
    op.status = 'cancelled'
    await db.put(STORE, op)
    notify()
  }

  async function update(id: string, patch: Partial<QueuedOp>): Promise<void> {
    const db = await getDB()
    const op = await db.get(STORE, id)
    if (!op) return
    Object.assign(op, patch)
    await db.put(STORE, op)
    notify()
  }

  async function count(): Promise<number> {
    const db = await getDB()
    const all = await db.getAll(STORE)
    return all.filter((op) => op.status !== 'cancelled').length
  }

  function subscribe(listener: () => void) {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }

  return { enqueue, list, getById, remove, cancel, update, count, subscribe }
}

let _store: OfflineQueueStore | null = null
export function getOfflineQueueStore(): OfflineQueueStore {
  if (!_store) _store = makeStore()
  return _store
}
